import React, { useState, useEffect } from 'react';
import { useTheme, useMediaQuery, TextField, Box, Button, 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    FormControl, InputLabel, Select, MenuItem,
    Autocomplete,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import FlexBetween from 'components/FlexBetween';


const RouteFilterDialog = ({
    open,
    onClose,
    onApply,
    acTypeOptions,
    flightRegOptions,
    depOptions,
    arrOptions,
    currentFilters,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const [cleared, setCleared] = React.useState(false);

    useEffect(() => {
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);

            return () => clearTimeout(timeout);
        }
        return () => {};
    }, [cleared]);

    const initialFilterValues = {
        routeDate: null,
        routeNo: '',
        crewRoute: '',
        flightNo: '',
        acType: [],
        flightReg: [],
        dep: '',
        arr: '',
    };

    const [filterValues, setFilterValues] = useState(() => {
        if (!currentFilters) return initialFilterValues;
        return {
            ...initialFilterValues,
            ...currentFilters,
            acType: currentFilters.acType ?? [],
            flightReg: currentFilters.flightReg ?? [],
        };
    });

    const handleDateChange = (date) => {
        setFilterValues(prev => ({ 
            ...prev, 
            routeDate: date 
        }));
    };

    const handleFilterChange = (field) => (event) => {
        const value = event.target.value;
        setFilterValues(prev => ({
            ...prev,
            [field]: value, 
        }));
    };     

    const handleCancel = () => {
        setFilterValues(initialFilterValues);  
        onApply(null);
        onClose();  
    };
    
    const handleApply = () => {
        const appliedFilters = {
            ...filterValues,
            acType: filterValues.acType.length ? filterValues.acType : null,
            flightReg: filterValues.flightReg.length ? filterValues.flightReg : null,
        };
        onApply(appliedFilters);
        onClose();
    };    

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleApply();
        }
    };

    return (   
        <LocalizationProvider 
            dateAdapter={AdapterDateFns}
            adapterLocale={locales['en-gb']}
        >     
            <Dialog 
                open={open} 
                onClose={handleCancel}
                sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '80%' : '40%' } }}
            >
                <DialogTitle>Filter</DialogTitle>
                <DialogContent onKeyDown={handleKeyPress}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <FlexBetween
                            gap={2}
                            sx={{ marginTop: '6px' }}
                        >
                            <DatePicker
                                label="Route Date"
                                value={filterValues.routeDate}
                                onChange={handleDateChange}
                                format="dd/MM/yyyy"
                                sx={{ width: '50%' }}
                                slotProps={{
                                    field: { clearable: true, onClear: () => setCleared(true) },
                                }}
                            />
                            
                            <TextField
                                label="Route No"
                                value={filterValues.routeNo}
                                onChange={handleFilterChange('routeNo')}
                                sx={{ width: '50%' }}
                            />
                        </FlexBetween> 
                        
                        <FlexBetween gap={2}>
                            <TextField
                                fullWidth
                                label="Crew Route"
                                value={filterValues.crewRoute}
                                onChange={(event) => {
                                    const value = event.target.value.toUpperCase();
                                    setFilterValues((prev) => ({ ...prev, crewRoute: value }));
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Flight No"
                                value={filterValues.flightNo}
                                onChange={handleFilterChange('flightNo')}
                            />
                        </FlexBetween>                        

                        <FlexBetween gap={2}>          
                            <Autocomplete
                                options={depOptions || []} 
                                getOptionLabel={(option) => option}
                                value={filterValues.dep || null}
                                onChange={(event, newValue) => {
                                    setFilterValues((prev) => ({ ...prev, dep: newValue ? newValue.toUpperCase() : '' }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Departure"
                                        variant="filled"
                                    />
                                )}
                                sx={{ width: '50%' }}
                            />

                            <Autocomplete
                                options={arrOptions || []}
                                getOptionLabel={(option) => option}
                                value={filterValues.arr || null}
                                onChange={(event, newValue) => {
                                    setFilterValues((prev) => ({ ...prev, arr: newValue ? newValue.toUpperCase() : '' }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Arrival"
                                        variant="filled"
                                    />
                                )}
                                sx={{ width: '50%' }}
                            />                           
                        </FlexBetween>

                        <FlexBetween gap={2}>                            
                            <FormControl fullWidth>
                                <InputLabel>AC Type</InputLabel>
                                <Select
                                    multiple
                                    label="AC Type"
                                    value={filterValues.acType}
                                    onChange={handleFilterChange('acType')}
                                    renderValue={(selected) => selected.join(', ')}
                                >
                                    {acTypeOptions.map((type, index) => (
                                        <MenuItem key={index} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>Flight Reg</InputLabel>
                                <Select
                                    multiple
                                    label="Flight Reg"
                                    value={filterValues.flightReg}
                                    onChange={handleFilterChange('flightReg')}
                                    renderValue={(selected) => selected.join(', ')}
                                >
                                    {flightRegOptions.map((reg, index) => (
                                        <MenuItem key={index} value={reg}>
                                            {reg}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>                            
                        </FlexBetween>                                                
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={handleCancel} 
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleApply} 
                        variant="outlined"
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default RouteFilterDialog;
